<template>
  <div class="px-2">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#1e293b"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />

    <v-snackbar v-model="showsnackbar" color="#0f172a" right :timeout="timeout">
      <span style="color: #FFFFFF; font-weight: 600;">{{ msg }}</span>
      <template v-slot:action="{ attrs }">
        <v-btn text color="#FFFFFF" v-bind="attrs" @click="showsnackbar = false">
          <v-icon style="color: #FFFFFF">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <!-- EXECUTIVE HEADER -->
    <header class="sa-hero-header mb-4">
      <div class="sa-hero-text">
        <PageBreadcrumb :items="[{ text: 'Jewellery Stores' }]" class="mb-1" />
        <div class="d-flex align-center flex-wrap gap-2">
          <span class="role-badge-pill">Store Administrators</span>
        </div>
        <p class="sa-subtitle">
          Manage registered jewellery stores, their administrators, contact information, and access
        </p>
      </div>

      <div class="sa-hero-actions">
        <button class="action-btn outline-btn mr-2" @click="getData()" title="Refresh List">
          <v-icon left size="16" color="#334155">mdi-refresh</v-icon>
          Refresh
        </button>
        <button class="action-btn primary-dark-btn" @click="$router.push('/superadminDashboard')">
          <v-icon left size="16" color="#ffffff">mdi-plus</v-icon>
          Add Jewellery Admin
        </button>
      </div>
    </header>

    <!-- PRO KPI RIBBON CARDS -->
    <v-row class="mb-4">
      <v-col cols="12" sm="6" md="3" class="py-1">
        <v-card outlined class="pa-3 rounded-lg flex-column justify-center" color="#FFFFFF">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="kpi-title">TOTAL REGISTERED ADMINS</span>
            <v-icon small color="#334155">mdi-store-outline</v-icon>
          </div>
          <div class="kpi-val">{{ totalCount }} <small class="text-caption">Accounts</small></div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3" class="py-1">
        <v-card outlined class="pa-3 rounded-lg flex-column justify-center" color="#FFFFFF">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="kpi-title">PAGE DISPLAYED</span>
            <v-icon small color="#2563EB">mdi-file-document-outline</v-icon>
          </div>
          <div class="kpi-val text-blue">Page {{ page }} of {{ Pagelength || 1 }}</div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3" class="py-1">
        <v-card outlined class="pa-3 rounded-lg flex-column justify-center" color="#FFFFFF">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="kpi-title">SYSTEM STATUS</span>
            <v-icon small color="#047857">mdi-check-circle-outline</v-icon>
          </div>
          <div class="kpi-val text-green text-subtitle-1 font-weight-bold">All Accounts Active</div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3" class="py-1">
        <v-card outlined class="pa-3 rounded-lg flex-column justify-center" color="#FFFFFF">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="kpi-title">SECURITY LEVEL</span>
            <v-icon small color="#D97706">mdi-shield-check-outline</v-icon>
          </div>
          <div class="kpi-val text-amber text-subtitle-1 font-weight-bold">Encrypted Auth</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- FILTER & SEARCH BAR -->
    <div class="filter-search-wrap mb-4">
      <div class="search-input-wrap" style="width: 100%; max-width: 380px;">
        <v-text-field
          v-model="searchQuery"
          prepend-inner-icon="mdi-magnify"
          placeholder="Filter by store name, email, phone, or username..."
          outlined
          dense
          hide-details
          clearable
          class="search-field"
        ></v-text-field>
      </div>
    </div>

    <!-- NO DATA STATE -->
    <div v-if="!appLoading && filteredAdminList.length === 0" class="no-data-box text-center py-8 my-4">
      <v-icon color="#64748b" large class="mb-2">mdi-account-off-outline</v-icon>
      <div class="no-data-text">No registered jewellery admins found matching your filter criteria.</div>
    </div>

    <!-- MAIN ADMIN DIRECTORY TABLE CARD -->
    <v-card v-if="filteredAdminList.length > 0" outlined class="rounded-lg overflow-hidden elevation-1 mb-4" color="#FFFFFF">
      <div class="table-container">
        <table class="pro-admin-table">
          <thead>
            <tr>
              <th class="text-center" style="width: 60px;">#</th>
              <th class="text-left sticky-col">JEWELLERY STORE &amp; USERNAME</th>
              <th class="text-left">CONTACT DETAILS</th>
              <th class="text-left">STORE LOCATION</th>
              <th class="text-center">LAST LOGIN</th>
              <th class="text-center">STATUS</th>
              <th class="text-center" style="width: 120px;">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(value, i) in filteredAdminList" :key="value._id || i" class="table-row">
              <!-- S.NO -->
              <td class="text-center font-weight-semibold text-muted">
                {{ (page - 1) * 20 + i + 1 }}
              </td>

              <!-- STORE & USERNAME -->
              <td class="text-left sticky-col font-weight-bold">
                <div class="d-flex align-center">
                  <v-avatar color="#F1F5F9" size="34" class="mr-3">
                    <span class="avatar-text">{{ (value.jewelleryName || value.username || 'A').charAt(0).toUpperCase() }}</span>
                  </v-avatar>
                  <div>
                    <div class="store-name">{{ value.jewelleryName || 'Jewellery Store' }}</div>
                    <div class="username-text">
                      <v-icon size="12" color="#64748b" class="mr-1">mdi-account-outline</v-icon>
                      {{ value.username || '-' }}
                    </div>
                  </div>
                </div>
              </td>

              <!-- CONTACT DETAILS -->
              <td class="text-left">
                <div class="contact-line">
                  <v-icon size="13" color="#64748b" class="mr-1">mdi-email-outline</v-icon>
                  <span>{{ value.email || '-' }}</span>
                </div>
                <div class="contact-line text-muted mt-1" v-if="value.phone">
                  <v-icon size="13" color="#64748b" class="mr-1">mdi-phone-outline</v-icon>
                  <span>{{ value.phone }}</span>
                </div>
              </td>

              <!-- STORE LOCATION -->
              <td class="text-left max-location-col">
                <div class="location-text" :title="value.location">
                  <v-icon size="13" color="#64748b" class="mr-1">mdi-map-marker-outline</v-icon>
                  <span>{{ value.location || 'Not Specified' }}</span>
                </div>
              </td>

              <!-- LAST LOGIN -->
              <td class="text-center">
                <span class="text-muted" style="font-size: 12px;">{{ formatLastLogin(value.stats && value.stats.lastLogin) }}</span>
              </td>

              <!-- STATUS -->
              <td class="text-center">
                <span class="pro-status-badge badge-active">Active</span>
              </td>

              <!-- ACTIONS -->
              <td class="text-center">
                <div class="d-flex align-center justify-center gap-1">
                  <!-- View Details -->
                  <v-btn
                    x-small
                    icon
                    color="#0F172A"
                    @click="openViewDetailsDialog(value)"
                    title="View Store Details"
                  >
                    <v-icon size="17">mdi-eye-outline</v-icon>
                  </v-btn>

                  <!-- Edit Admin -->
                  <v-btn
                    x-small
                    icon
                    color="#2563EB"
                    @click="openEditDialog(value)"
                    title="Edit Admin Details"
                  >
                    <v-icon size="17">mdi-pencil-outline</v-icon>
                  </v-btn>

                  <!-- Reset Password -->
                  <v-btn
                    x-small
                    icon
                    color="#D97706"
                    @click="openResetPasswordDialog(value._id)"
                    title="Reset Password"
                  >
                    <v-icon size="17">mdi-lock-reset</v-icon>
                  </v-btn>

                  <!-- Delete Admin -->
                  <v-btn
                    x-small
                    icon
                    color="#EF4444"
                    @click="openDeleteDialog(value._id)"
                    title="Delete Admin"
                  >
                    <v-icon size="17">mdi-delete-outline</v-icon>
                  </v-btn>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- PAGINATION BAR -->
      <div class="pa-4 d-flex align-center justify-space-between flex-wrap" style="border-top: 1px solid #E2E8F0;">
        <span class="text-caption text-muted font-weight-medium">
          Showing Page {{ page }} of {{ Pagelength }} ({{ totalCount }} Total Registered Admins)
        </span>
        <v-pagination
          small
          color="#0F172A"
          v-model="page"
          :length="Pagelength"
          total-visible="7"
        ></v-pagination>
      </div>
    </v-card>

    <!-- EDIT ADMIN DIALOG -->
    <v-dialog v-model="editdialogue" max-width="520px" persistent>
      <v-card class="rounded-xl overflow-hidden pa-0" color="#FFFFFF">
        <div class="dialog-header px-5 py-4 d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon size="20" color="#2563EB" class="mr-2">mdi-account-edit-outline</v-icon>
            <h3 class="dialog-title">Edit Admin Profile</h3>
          </div>
          <button class="close-btn" @click="editdialogue = false">
            <v-icon size="18" color="#64748b">mdi-close</v-icon>
          </button>
        </div>

        <v-divider></v-divider>

        <v-form ref="form2" v-model="valid2" class="pa-5">
          <v-layout wrap>
            <v-flex xs12 pa-2>
              <label class="field-label">Jewellery Store Name <span class="text-danger">*</span></label>
              <v-text-field
                v-model="curItem.jewelleryName"
                placeholder="Store Name"
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-flex>

            <v-flex xs12 sm6 pa-2>
              <label class="field-label">Admin Username <span class="text-danger">*</span></label>
              <v-text-field
                v-model="curItem.username"
                placeholder="Username"
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-flex>

            <v-flex xs12 sm6 pa-2>
              <label class="field-label">Phone Number <span class="text-danger">*</span></label>
              <v-text-field
                v-model="curItem.phone"
                type="number"
                placeholder="Phone Number"
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-flex>

            <v-flex xs12 pa-2>
              <label class="field-label">Email Address <span class="text-danger">*</span></label>
              <v-text-field
                v-model="curItem.email"
                type="email"
                placeholder="Email Address"
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-flex>

            <v-flex xs12 pa-2>
              <label class="field-label">Physical Address &amp; Location <span class="text-danger">*</span></label>
              <v-textarea
                v-model="curItem.location"
                placeholder="Shop address, city, state"
                outlined
                dense
                rows="3"
                hide-details="auto"
              ></v-textarea>
            </v-flex>
          </v-layout>
        </v-form>

        <v-divider></v-divider>

        <div class="px-5 py-3 dialog-footer d-flex align-center justify-end gap-2">
          <v-btn text height="36" class="text-none font-weight-bold" color="#64748b" @click="editdialogue = false">
            Cancel
          </v-btn>
          <v-btn
            color="#0F172A"
            dark
            height="36"
            elevation="1"
            class="text-none font-weight-bold px-4"
            style="border-radius: 8px;"
            @click="validateInput()"
            :disabled="appLoading"
          >
            Save Changes
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- DELETE CONFIRMATION DIALOG -->
    <v-dialog v-model="deletedialog" max-width="420px">
      <v-card class="rounded-xl overflow-hidden pa-0" color="#FFFFFF">
        <div class="pa-5 text-center">
          <v-avatar color="#FEE2E2" size="48" class="mb-3">
            <v-icon color="#EF4444" size="24">mdi-alert-outline</v-icon>
          </v-avatar>
          <h3 class="dialog-title mb-1">Delete Store Admin?</h3>
          <p class="text-body-2 text-muted mb-0">
            Are you sure you want to delete this jewellery admin account? This action cannot be undone.
          </p>
        </div>

        <v-divider></v-divider>

        <div class="px-5 py-3 dialog-footer d-flex align-center justify-end gap-2">
          <v-btn text height="36" class="text-none font-weight-bold" color="#64748b" @click="deletedialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="#EF4444"
            dark
            height="36"
            elevation="1"
            class="text-none font-weight-bold px-4"
            style="border-radius: 8px;"
            @click="deleteCategory()"
            :disabled="appLoading"
          >
            Delete Admin
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- RESET PASSWORD CONFIRMATION DIALOG -->
    <v-dialog v-model="resetPasswordDialog" max-width="420px">
      <v-card class="rounded-xl overflow-hidden pa-0" color="#FFFFFF">
        <div class="pa-5 text-center">
          <v-avatar color="#FEF3C7" size="48" class="mb-3">
            <v-icon color="#D97706" size="24">mdi-lock-reset</v-icon>
          </v-avatar>
          <h3 class="dialog-title mb-1">Reset Admin Password?</h3>
          <p class="text-body-2 text-muted mb-0">
            A new random password will be generated and emailed to this jewellery admin. They will be required to change it on next login.
          </p>
        </div>

        <v-divider></v-divider>

        <div class="px-5 py-3 dialog-footer d-flex align-center justify-end gap-2">
          <v-btn text height="36" class="text-none font-weight-bold" color="#64748b" @click="resetPasswordDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="#D97706"
            dark
            height="36"
            elevation="1"
            class="text-none font-weight-bold px-4"
            style="border-radius: 8px;"
            @click="resetPassword()"
            :disabled="appLoading"
          >
            Reset Password
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- VIEW STORE DETAILS DIALOG -->
    <v-dialog v-model="viewDetailsDialog" max-width="480px">
      <v-card class="rounded-xl overflow-hidden pa-0" color="#FFFFFF">
        <div class="dialog-header px-5 py-4 d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon size="20" color="#0F172A" class="mr-2">mdi-store-outline</v-icon>
            <h3 class="dialog-title">Store Details</h3>
          </div>
          <button class="close-btn" @click="viewDetailsDialog = false">
            <v-icon size="18" color="#64748b">mdi-close</v-icon>
          </button>
        </div>

        <v-divider></v-divider>

        <div class="pa-5">
          <div class="d-flex align-center mb-4">
            <v-avatar color="#F1F5F9" size="44" class="mr-3">
              <span class="avatar-text">{{ (viewItem.jewelleryName || viewItem.username || 'A').charAt(0).toUpperCase() }}</span>
            </v-avatar>
            <div>
              <div class="store-name">{{ viewItem.jewelleryName || 'Jewellery Store' }}</div>
              <div class="username-text">
                <v-icon size="12" color="#64748b" class="mr-1">mdi-account-outline</v-icon>
                {{ viewItem.username || '-' }}
              </div>
            </div>
          </div>

          <div class="detail-row">
            <v-icon size="15" color="#64748b" class="mr-2">mdi-email-outline</v-icon>
            <span>{{ viewItem.email || '-' }}</span>
          </div>
          <div class="detail-row" v-if="viewItem.phone">
            <v-icon size="15" color="#64748b" class="mr-2">mdi-phone-outline</v-icon>
            <span>{{ viewItem.phone }}</span>
          </div>
          <div class="detail-row">
            <v-icon size="15" color="#64748b" class="mr-2">mdi-map-marker-outline</v-icon>
            <span>{{ viewItem.location || 'Not Specified' }}</span>
          </div>
          <div class="detail-row">
            <v-icon size="15" color="#64748b" class="mr-2">mdi-clock-outline</v-icon>
            <span>Last login: {{ formatLastLogin(viewItem.stats && viewItem.stats.lastLogin) }}</span>
          </div>

          <v-divider class="my-4"></v-divider>

          <div class="usage-detail-grid" v-if="viewItem.stats">
            <div class="usage-detail-card">
              <v-icon size="20" color="#2563EB">mdi-account-multiple-outline</v-icon>
              <div class="usage-detail-value">{{ viewItem.stats.staffCount }}</div>
              <div class="usage-detail-label">Staff Members</div>
            </div>
            <div class="usage-detail-card">
              <v-icon size="20" color="#0D9488">mdi-receipt-text-outline</v-icon>
              <div class="usage-detail-value">{{ viewItem.stats.invoiceCount }}</div>
              <div class="usage-detail-label">Invoices Issued</div>
            </div>
            <div class="usage-detail-card">
              <v-icon size="20" color="#D97706">mdi-package-variant-closed</v-icon>
              <div class="usage-detail-value">{{ viewItem.stats.stockCount }}</div>
              <div class="usage-detail-label">Active Stock Items</div>
            </div>
          </div>
        </div>

        <v-divider></v-divider>

        <div class="px-5 py-3 dialog-footer d-flex align-center justify-end gap-2">
          <v-btn text height="36" class="text-none font-weight-bold" color="#64748b" @click="viewDetailsDialog = false">
            Close
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "ViewAdminPage",
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2500,
      msg: null,
      page: 1,
      Pagelength: 1,
      totalCount: 0,
      deletedialog: false,
      resetPasswordDialog: false,
      curId: null,
      editdialogue: false,
      curItem: {},
      viewDetailsDialog: false,
      viewItem: {},
      valid2: false,
      adminList: [],
      searchQuery: "",
    };
  },
  computed: {
    filteredAdminList() {
      let list = this.adminList;
      if (this.searchQuery && this.searchQuery.trim()) {
        const q = this.searchQuery.trim().toLowerCase();
        list = list.filter(
          item =>
            (item.jewelleryName || "").toLowerCase().includes(q) ||
            (item.username || "").toLowerCase().includes(q) ||
            (item.email || "").toLowerCase().includes(q) ||
            (item.phone || "").toString().includes(q) ||
            (item.location || "").toLowerCase().includes(q)
        );
      }
      return list;
    }
  },
  mounted() {
    this.getData();
  },
  watch: {
    page() {
      this.getData();
    }
  },
  methods: {
    formatLastLogin(date) {
      if (!date) return "Never";
      var d = new Date(date);
      return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    },
    openEditDialog(item) {
      this.curItem = { ...item };
      this.editdialogue = true;
    },
    openViewDetailsDialog(item) {
      this.viewItem = { ...item };
      this.viewDetailsDialog = true;
    },
    openDeleteDialog(id) {
      this.curId = id;
      this.deletedialog = true;
    },
    openResetPasswordDialog(id) {
      this.curId = id;
      this.resetPasswordDialog = true;
    },
    resetPassword() {
      this.appLoading = true;
      axios({
        url: "/user/reset/password",
        method: "POST",
        headers: {
          "x-auth-token": localStorage.getItem("token")
        },
        data: { id: this.curId }
      })
        .then(response => {
          this.resetPasswordDialog = false;
          this.appLoading = false;
          if (response.data && response.data.status === true) {
            this.msg = response.data.msg || "Password reset. New password emailed to the admin.";
            this.showsnackbar = true;
          } else {
            this.msg = (response.data && response.data.msg) || "Failed to reset password.";
            this.showsnackbar = true;
          }
        })
        .catch(err => {
          this.resetPasswordDialog = false;
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
    getData() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/user/all/jewelleryadmins",
        headers: {
          "x-auth-token": localStorage.getItem("token")
        },
        params: {
          keyword: this.searchQuery,
          page: this.page,
          count: 20
        }
      })
        .then(response => {
          this.appLoading = false;
          if (response.data && response.data.status === true) {
            this.adminList = response.data.data || [];
            this.totalCount = response.data.count || this.adminList.length;
            this.Pagelength = Math.ceil(this.totalCount / 20) || 1;
          } else {
            this.msg = (response.data && response.data.msg) || "Failed to load admin directory.";
            this.showsnackbar = true;
          }
        })
        .catch(err => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
    validateInput() {
      if (!this.curItem.email) {
        this.msg = "Please provide email address";
        this.showsnackbar = true;
        return;
      }
      if (!this.curItem.jewelleryName) {
        this.msg = "Please provide store name";
        this.showsnackbar = true;
        return;
      }
      if (!this.curItem.phone) {
        this.msg = "Please provide phone number";
        this.showsnackbar = true;
        return;
      }
      if (!this.curItem.username) {
        this.msg = "Please provide username";
        this.showsnackbar = true;
        return;
      }
      if (!this.curItem.location) {
        this.msg = "Please provide store address";
        this.showsnackbar = true;
        return;
      }
      this.editCategory();
    },
    editCategory() {
      this.appLoading = true;
      axios({
        url: "/user/adminedit",
        method: "POST",
        headers: {
          "x-auth-token": localStorage.getItem("token")
        },
        data: {
          id: this.curItem._id,
          username: this.curItem.username,
          email: this.curItem.email,
          jewelleryName: this.curItem.jewelleryName,
          phone: this.curItem.phone,
          location: this.curItem.location
        }
      })
        .then(response => {
          this.editdialogue = false;
          this.appLoading = false;
          if (response.data && response.data.status === true) {
            this.msg = response.data.msg || "Admin profile updated successfully";
            this.showsnackbar = true;
            this.getData();
          } else {
            this.msg = (response.data && response.data.msg) || "Failed to update admin profile.";
            this.showsnackbar = true;
          }
        })
        .catch(err => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
    deleteCategory() {
      this.appLoading = true;
      axios({
        url: "/user/remove",
        method: "GET",
        headers: {
          "x-auth-token": localStorage.getItem("token")
        },
        params: {
          id: this.curId
        }
      })
        .then(response => {
          this.deletedialog = false;
          this.appLoading = false;
          if (response.data && response.data.status === true) {
            this.msg = response.data.msg || "Admin account removed";
            this.showsnackbar = true;
            this.getData();
          } else {
            this.msg = (response.data && response.data.msg) || "Failed to delete admin.";
            this.showsnackbar = true;
          }
        })
        .catch(err => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

/* Executive Header */
.sa-hero-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.sa-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #64748b;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.sa-main-title {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.3px;
  margin: 0;
  line-height: 1.2;
}

.role-badge-pill {
  display: inline-flex;
  align-items: center;
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  color: #334155;
  font-size: 11.5px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 6px;
  margin-left: 8px;
}

.sa-subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0 0;
}

.sa-hero-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.action-btn {
  height: 38px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
}

.outline-btn {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  color: #334155;
}

.outline-btn:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.primary-dark-btn {
  background: #0f172a;
  color: #ffffff;
  border: none;
}

.primary-dark-btn:hover {
  background: #1e293b;
}

/* KPI Cards */
.kpi-title {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: #64748b;
  text-transform: uppercase;
}

.kpi-val {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
}

.text-amber { color: #D97706; }
.text-blue { color: #2563EB; }
.text-green { color: #047857; }

/* Filter & Search */
.filter-search-wrap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  background: #FFFFFF;
  padding: 10px 14px;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
}

.no-data-box {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
}

.no-data-text {
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
}

/* Table Styling */
.table-container {
  overflow-x: auto;
  width: 100%;
}

.pro-admin-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13.5px;
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.pro-admin-table th {
  background: #F8FAFC;
  color: #475569;
  font-size: 11.5px;
  font-weight: 700;
  padding: 10px 16px;
  vertical-align: middle;
  border-bottom: 1px solid #E2E8F0;
  white-space: nowrap;
  letter-spacing: 0.3px;
}

.pro-admin-table td {
  padding: 12px 16px;
  vertical-align: middle;
  border-bottom: 1px solid #F1F5F9;
  white-space: nowrap;
  color: #0F172A;
}

.table-row:hover td {
  background-color: #F8FAFC !important;
}

.table-row:hover .sticky-col {
  background-color: #F8FAFC !important;
}

.sticky-col {
  position: sticky;
  left: 0;
  z-index: 2;
  background: #FFFFFF !important;
  border-right: 2px solid #E2E8F0 !important;
  min-width: 220px;
}

th.sticky-col {
  background: #F8FAFC !important;
  color: #475569 !important;
}

.avatar-text {
  font-size: 13px;
  font-weight: 700;
  color: #334155;
}

.store-name {
  font-size: 13.5px;
  font-weight: 700;
  color: #0F172A;
}

.username-text {
  font-size: 12px;
  color: #64748B;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.contact-line {
  font-size: 12.5px;
  color: #334155;
  display: flex;
  align-items: center;
}

.max-location-col {
  max-width: 240px;
  white-space: normal !important;
}

.location-text {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 12.5px;
  color: #475569;
  line-height: 1.4;
}

.detail-row {
  display: flex;
  align-items: center;
  font-size: 13.5px;
  color: #334155;
  margin-bottom: 10px;
}

.usage-detail-grid {
  display: flex;
  gap: 10px;
}

.usage-detail-card {
  flex: 1;
  text-align: center;
  padding: 12px 8px;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  background: #F8FAFC;
}

.usage-detail-value {
  font-size: 18px;
  font-weight: 700;
  color: #0F172A;
  margin-top: 4px;
}

.usage-detail-label {
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
}

.pro-status-badge {
  display: inline-block;
  padding: 3px 12px;
  border-radius: 16px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.2px;
}

.badge-active {
  background: #ECFDF5;
  color: #047857;
  border: 1px solid #6EE7B7;
}

.field-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 4px;
}

.text-danger {
  color: #ef4444;
}

/* Dialog Styling */
.dialog-header {
  background: #F8FAFC;
}

.dialog-title {
  font-size: 17px;
  font-weight: 700;
  color: #0F172A;
  margin: 0;
}

.close-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
}

.close-btn:hover {
  background: #E2E8F0;
}

.dialog-footer {
  background: #F8FAFC;
}
</style>
